import React, { useState, useEffect } from "react";
import EmployeeModal from "./EmployeeModal";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [search, setSearch] = useState("");
  const [modalData, setModalData] = useState({ show: false, employee: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const itemsPerPage = 10;

  useEffect(() => {
    fetchEmployees();
    fetchDepartmentsAndPositions();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const res = await axiosClient.get(API_ENDPOINTS.employees);
      setEmployees(res.data);
    } catch (error) {
      console.error("❌ Lỗi khi tải danh sách nhân viên:", error);
      setToast("Lỗi khi tải danh sách nhân viên");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentsAndPositions = async () => {
    try {
      const depRes = await axiosClient.get(API_ENDPOINTS.departments);
      setDepartments(depRes.data);
      const posRes = await axiosClient.get(API_ENDPOINTS.positions);
      setPositions(posRes.data);
    } catch (error) {
      console.error("❌ Lỗi khi tải phòng ban hoặc chức vụ:", error);
      setToast("Lỗi khi tải phòng ban hoặc chức vụ");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa nhân viên này?")) return;
    try {
      await axiosClient.delete(`${API_ENDPOINTS.employees}/${id}`);
      setEmployees((prev) => prev.filter((emp) => emp.id !== id));
      setToast("Xóa nhân viên thành công");
    } catch (error) {
      console.error("❌ Lỗi khi xóa nhân viên:", error);
      setToast("Lỗi khi xóa nhân viên");
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await axiosClient.get(`${API_ENDPOINTS.employees}/${id}`);
      setModalData({ show: true, employee: res.data });
    } catch (error) {
      console.error("❌ Lỗi khi lấy chi tiết nhân viên:", error);
      setToast("Lỗi khi tải chi tiết nhân viên");
    }
  };

  const handleSave = async (employeeData) => {
    try {
      let response;

      if (modalData.employee) {
        // Cập nhật nhân viên
        response = await axiosClient.put(
          `${API_ENDPOINTS.employees}/${modalData.employee.id}`,
          employeeData
        );
        setToast("Cập nhật nhân viên thành công!");
      } else {
        // Thêm mới nhân viên
        response = await axiosClient.post(API_ENDPOINTS.employees, employeeData);
        setEmployees((prev) => [...prev, response.data]);
        setToast("Thêm nhân viên thành công!");
      }

      // Đóng modal sau khi thành công
      setModalData({ show: false, employee: null });

      // Sau khi thêm/sửa thành công, không cần fetch lại danh sách nếu không thay đổi nhiều
       fetchEmployees();
    } catch (error) {
      console.error("❌ Lỗi khi lưu nhân viên:", error);
      setToast("Lỗi khi lưu nhân viên");
    }
  };

  const filteredEmployees = employees.filter((emp) =>
    emp.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const paginatedEmployees = filteredEmployees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="container-fluid">
      <h1 className="h3 mb-4 text-gray-800">Danh sách nhân viên</h1>

      {toast && (
        <div className="alert alert-info d-flex justify-content-between">
          {toast}
          <button className="btn btn-sm btn-light" onClick={() => setToast("")}>
            &times;
          </button>
        </div>
      )}

      <div className="card shadow mb-4">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-primary">Nhân viên</h6>
          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              className="form-control"
              style={{ width: "200px" }}
              placeholder="Tìm kiếm..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              className="btn btn-success"
              onClick={() => setModalData({ show: true, employee: null })}
            >
              Thêm mới
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">Loading...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="thead-light">
                  <tr>
                    <th>ID</th>
                    <th>Tên</th>
                    <th>Phòng ban</th>
                    <th>Chức vụ</th>
                    <th>Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedEmployees.length ? (
                    paginatedEmployees.map((emp) => (
                      <tr key={emp.id}>
                        <td>{emp.id}</td>
                        <td>{emp.name}</td>
                        <td>
                          {departments.find((d) => d.departmentCode === emp.departmentCode)?.name ||
                            emp.departmentCode}
                        </td>
                        <td>
                          {positions.find((p) => p.positionCode === emp.positionCode)?.name ||
                            emp.positionCode}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary mr-2"
                            onClick={() => handleEdit(emp.id)}
                          >
                            Sửa
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(emp.id)}
                          >
                            Xóa
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        Không tìm thấy nhân viên
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="d-flex justify-content-center mt-3">
            <nav>
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  >
                    Trước
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => (
                  <li
                    key={i + 1}
                    className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                  >
                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages || totalPages === 0 ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  >
                    Sau
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      <EmployeeModal
        show={modalData.show}
        employee={modalData.employee}
        onClose={() => setModalData({ show: false, employee: null })}
        onSave={handleSave}
        departments={departments}
        positions={positions}
      />
    </div>
  );
};

export default EmployeeList;
